import React from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import {
  ZoomOut as ZoomOutIcon,
  ZoomIn as ZoomInIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'

import { LightTooltip, tooltipContent } from '../../../components/LightToolTip'
import SavingIndicator from '../../../components/SavingIndicator'

import {
  selectPage,
  selectPageIsMine,
  selectPageOwnerLogin,
  zoomInPage,
  zoomOutPage,
  updatePageAsync,
} from './pagesSlice'
import ShareButton from '../shares/ShareButton'

const classes = {
  // One row that keeps its size: without it the zoom controls wrapped onto a
  // second line as soon as the window got narrow.
  box: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  // The same Button the Share button uses, so that a page shared with me is the
  // same height as one of my own and the top bar doesn't change size. It just
  // doesn't do anything.
  readOnlyNote: {
    color: 'white',
    backgroundColor: 'inherit',
    cursor: 'default',
    // It borrows a Button's box model, not its shouting: MUI uppercases button
    // labels, and this is a label.
    textTransform: 'none',
    '& .MuiSvgIcon-root': {
      marginRight: '0.3em',
    },
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  zoomText: {
    display: { xs: 'none', sm: 'inline' },
  },
  zoomButton: {
    color: (theme) => theme.palette.common.white,
    margin: [0, 1],
    '&:hover': {
      backgroundColor: (theme) => theme.palette.info.light,
    },
  },
}

export default function PageButtons({ pageId }) {
  const page = useSelector(selectPage(pageId))
  const isMine = useSelector(selectPageIsMine(pageId))
  const ownerLogin = useSelector(selectPageOwnerLogin(pageId))
  const dispatch = useDispatch()

  ///////////////////////////////////////////////
  //
  // Events
  //

  const handleZoomOutClick = (e) => {
    // console.log('<PageMenu />.handleZoomOutClick()')
    dispatch(zoomOutPage(pageId))
    dispatch(updatePageAsync(pageId))
  }

  const handleZoomInClick = (e) => {
    // console.log('<PageMenu />.handleZoomInClick()')
    dispatch(zoomInPage(pageId))
    dispatch(updatePageAsync(pageId))
  }

  ///////////////////////////////////////////////
  //
  // Layout
  //

  const zoomText = !isNaN(page.zoom) ? `${Math.round(page.zoom * 100)}%` : ''

  ///////////////////////////////////////////////
  //
  // Markup
  //

  return (
    <React.Fragment>
      <Box sx={classes.box}>
        {/* Sharing belongs to the owner. In its place, a page shared with me says
            whose it is. The zoom controls stay either way: zooming is how you
            read a page, and on someone else's it moves the view without saving
            (see `updatePageAsync`). */}
        {isMine ? (
          <ShareButton pageId={pageId} />
        ) : (
          <LightTooltip
            title={tooltipContent(
              'You can read this page, but not change it',
              <VisibilityIcon />
            )}
          >
            <Button component='span' disableRipple sx={classes.readOnlyNote}>
              <VisibilityIcon />
              {ownerLogin ? `Shared by ${ownerLogin} · read only` : 'Read only'}
            </Button>
          </LightTooltip>
        )}
        {/* These are icon-only, so they need a name of their own: the tooltip
            isn't one. */}
        <LightTooltip title={tooltipContent('zoom out', <ZoomOutIcon />)}>
          <IconButton
            sx={classes.zoomButton}
            aria-label='zoom out'
            onClick={handleZoomOutClick}
          >
            <ZoomOutIcon />
          </IconButton>
        </LightTooltip>
        {/* Hidden in a narrow window, so the menu bar stays on one line; the
            zoom buttons are what matter. */}
        <Typography variant='p' sx={classes.zoomText}>{zoomText}</Typography>
        <LightTooltip title={tooltipContent('zoom in', <ZoomInIcon />)}>
          <IconButton
            sx={classes.zoomButton}
            aria-label='zoom in'
            onClick={handleZoomInClick}
          >
            <ZoomInIcon />
          </IconButton>
        </LightTooltip>
      </Box>
      <SavingIndicator />
    </React.Fragment>
  )
}
